"use client";

import { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  X,
  Save,
  RotateCw,
  AlertCircle,
  Users,
  Mail,
  Phone,
  FileText,
  User,
  Type as TypeIcon,
  Percent,
  Check,
  Hash,
} from "lucide-react";
import { InputCustom, SearchableSelect } from "../../components";
import providersService from "../../services/providersService";

/* ────────────────────────────── Brand Icons ────────────────────────────── */
const TikTokIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#111827"
      fillRule="evenodd"
      d="M10.904,6h26.191C39.804,6,42,8.196,42,10.904v26.191 C42,39.804,39.804,42,37.096,42H10.904C8.196,42,6,39.804,6,37.096V10.904C6,8.196,8.196,6,10.904,6z"
      clipRule="evenodd"
    />
    <path
      fill="#ec407a"
      fillRule="evenodd"
      d="M29.208,20.607c1.576,1.126,3.507,1.788,5.592,1.788v-4.011 c-0.395,0-0.788-0.041-1.174-0.123v3.157c-2.085,0-4.015-0.663-5.592-1.788v8.184c0,4.094-3.321,7.413-7.417,7.413 c-1.528,0-2.949-0.462-4.129-1.254c1.347,1.376,3.225,2.23,5.303,2.23c4.096,0,7.417-3.319,7.417-7.413L29.208,20.607z M30.657,16.561c-0.805-0.879-1.334-2.016-1.449-3.273v-0.516h-1.113C28.375,14.369,29.331,15.734,30.657,16.561z M19.079,30.832c-0.45-0.59-0.693-1.311-0.692-2.053c0-1.873,1.519-3.391,3.393-3.391c0.349,0,0.696,0.053,1.029,0.159v-4.1 c-0.389-0.053-0.781-0.076-1.174-0.068v3.191c-0.333-0.106-0.68-0.159-1.03-0.159c-1.874,0-3.393,1.518-3.393,3.391 C17.213,29.127,17.972,30.274,19.079,30.832z"
      clipRule="evenodd"
    />
    <path
      fill="#fff"
      fillRule="evenodd"
      d="M28.034,19.63c1.576,1.126,3.507,1.788,5.592,1.788v-3.157 c-1.164-0.248-2.194-0.856-2.969-1.701c-1.326-0.827-2.281-2.191-2.561-3.788h-2.923v16.018c-0.007,1.867-1.523,3.379-3.393,3.379 c-1.102,0-2.081-0.525-2.701-1.338c-1.107-0.558-1.866-1.705-1.866-3.029c0-1.873,1.519-3.391,3.393-3.391 c0.359,0,0.705,0.056,1.03,0.159V21.38c-4.024,0.083-7.26,3.369-7.26,7.411c0,2.018,0.806,3.847,2.114,5.183 c1.18,0.792,2.601,1.254,4.129,1.254c4.096,0,7.417-3.319,7.417-7.413z"
      clipRule="evenodd"
    />
    <path
      fill="#7dd3fc"
      fillRule="evenodd"
      d="M33.626,18.262v-0.854c-1.05,0.002-2.078-0.292-2.969-0.848 C31.445,17.423,32.483,18.018,33.626,18.262z M28.095,12.772c-0.027-0.153-0.047-0.306-0.061-0.461v-0.516h-4.036v16.019 c-0.006,1.867-1.523,3.379-3.393,3.379c-0.549,0-1.067-0.13-1.526-0.362c0.62,0.813,1.599,1.338,2.701,1.338 c1.87,0,3.386-1.512,3.393-3.379V12.772H28.095z M21.635,21.38v-0.909c-0.337-0.046-0.677-0.069-1.018-0.069 c-4.097,0-7.417,3.319-7.417,7.413c0,2.567,1.305,4.829,3.288,6.159c-1.308-1.336-2.114-3.165-2.114-5.183 C14.374,24.749,17.611,21.463,21.635,21.38z"
      clipRule="evenodd"
    />
  </svg>
);

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <polygon fill="#fbbf24" points="30.129,15.75 18.871,9.25 5.871,31.25 17.129,37.75" />
    <path
      fill="#1d4ed8"
      d="M31.871,37.75c1.795,3.109,5.847,4.144,8.879,2.379c3.103-1.806,4.174-5.77,2.379-8.879l-13-22
      c-1.795-3.109-5.835-4.144-8.879-2.379c-3.106,1.801-4.174,5.77-2.379,8.879L31.871,37.75z"
    />
    <circle cx="11.5" cy="34.5" r="6.5" fill="#16a34a" />
  </svg>
);

const MetaIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#1d4ed8"
      d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5
      c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28
      c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51
      c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40
      c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35
      c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57
      c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5
      c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
    />
    <linearGradient id="meta_g1" x1="42.304" x2="13.533" y1="24.75" y2="24.75" gradientUnits="userSpaceOnUse">
      <stop offset="0" stopColor="#1d4ed8" />
      <stop offset=".995" stopColor="#1d4ed8" />
    </linearGradient>
    <path
      fill="url(#meta_g1)"
      d="M4.918,15.456
      C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967
      c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362
      c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333
      c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754
      c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
    />
  </svg>
);

const SnapchatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#facc15" d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z" />
    <path
      fill="#fff"
      d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705
      c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084
      c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954
      c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283
      c3.731-0.614,5.399-4.44,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174
      c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186
      c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364
      c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674
      C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673
      c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364
      c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186
      c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174
      c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283
      c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267
      c-0.142,0-0.319-0.039-0.526-0.084
      c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118c-0.393,0.089-0.84,0.388-1.317,0.705
      C28.045,34.91,26.451,36,24,36z"
    />
  </svg>
);

/* ────────────────────────────── Config ────────────────────────────── */
const PLATFORMS = [
  { id: "Facebook", name: "Facebook", Icon: MetaIcon },
  { id: "Google", name: "Google", Icon: GoogleIcon },
  { id: "TikTok", name: "TikTok", Icon: TikTokIcon },
  { id: "Snapchat", name: "Snapchat", Icon: SnapchatIcon },
];

const TYPES = [
  { id: "only_white_hat", value: "only_white_hat", name: "Only White Hat" },
  { id: "white_and_grey", value: "white_and_grey", name: "White and Grey" },
  { id: "all", value: "all", name: "All" },
  { id: "other", value: "other", name: "Other" },
];

/* ────────────────────────────── Helpers ────────────────────────────── */
const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const clamp = (num, min, max) => Math.min(max, Math.max(min, Number(num) || 0));
const formatDate = (d) =>
  d
    ? new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(d))
    : "";

const ensureFeesForPlatforms = (platforms, currentFees) => {
  const next = [...currentFees];
  const set = new Set(platforms);

  platforms.forEach((pl) => {
    if (!next.find((f) => f.platform === pl)) {
      next.push({ platform: pl, feePercentage: 0 });
    }
  });

  return next.filter((f) => set.has(f.platform));
};

/* ────────────────────────────── Component ────────────────────────────── */
export default function ProviderModal({
  isOpen,
  onClose,
  providerId = null,
  isEditing = false,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    platforms: [],
    type: "",
    customType: "",
    fees: [],
    createdAt: "",
    updatedAt: "",
    _id: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    platforms: "",
    type: "",
    fees: "",
    form: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    const run = async () => {
      try {
        setLoading(true);

        if (isEditing && providerId) {
          const { data } = await providersService.get(providerId);
          if (data?.statusCode === 200 && data?.data) {
            const p = data.data;

            const rawType = Array.isArray(p.type) ? p.type[0] : p.type || "";
            const knownType = TYPES.some((t) => t.value === rawType);

            setFormData({
              name: p.name || "",
              email: p.email || "",
              phone: p.phone || "",
              notes: p.notes || "",
              platforms: Array.isArray(p.platforms) ? p.platforms : [],
              type: knownType ? rawType : rawType ? "other" : "",
              customType: !knownType ? rawType : "",
              fees: Array.isArray(p.fees)
                ? p.fees.map((f) => ({
                  platform: f.platform,
                  feePercentage: Number(f.feePercentage ?? 0),
                }))
                : [],
              createdAt: p.createdAt || "",
              updatedAt: p.updatedAt || "",
              _id: p._id || "",
            });
          }
        } else {
          setFormData({
            name: "",
            email: "",
            phone: "",
            notes: "",
            platforms: [],
            type: "",
            customType: "",
            fees: [],
            createdAt: "",
            updatedAt: "",
            _id: "",
          });
          setErrors({
            name: "",
            email: "",
            platforms: "",
            type: "",
            fees: "",
            form: "",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [isOpen, isEditing, providerId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const togglePlatform = (platformId) => {
    const updated = formData.platforms.includes(platformId)
      ? formData.platforms.filter((p) => p !== platformId)
      : [...formData.platforms, platformId];

    const alignedFees = ensureFeesForPlatforms(updated, formData.fees);

    setFormData((prev) => ({ ...prev, platforms: updated, fees: alignedFees }));
    if (errors.platforms) setErrors((prev) => ({ ...prev, platforms: "" }));
    if (errors.fees) setErrors((prev) => ({ ...prev, fees: "" }));
  };

  const handleFeeChange = (platformId, value) => {
    const num = clamp(value, 0, 100);
    setFormData((prev) => ({
      ...prev,
      fees: ensureFeesForPlatforms(prev.platforms, prev.fees).map((f) =>
        f.platform === platformId ? { ...f, feePercentage: num } : f
      ),
    }));
    if (errors.fees) setErrors((prev) => ({ ...prev, fees: "" }));
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      platforms: "",
      type: "",
      fees: "",
      form: "",
    };
    let ok = true;

    if (!formData.name.trim()) {
      newErrors.name = "Provider name is required";
      ok = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      ok = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please provide a valid email";
      ok = false;
    }

    if (!formData.type) {
      newErrors.type = "Provider type is required";
      ok = false;
    } else if (formData.type === "other" && !formData.customType.trim()) {
      newErrors.type = "Please specify the provider type";
      ok = false;
    }

    if (formData.platforms.length === 0) {
      newErrors.platforms = "At least one platform is required";
      ok = false;
    }

    const feeMap = new Map(
      formData.fees.map((f) => [f.platform, Number(f.feePercentage)])
    );

    for (const pl of formData.platforms) {
      const pct = feeMap.get(pl);
      if (pct === undefined || pct === null || Number.isNaN(pct)) {
        newErrors.fees = `Fee % is required for ${pl}`;
        ok = false;
        break;
      }
      if (pct < 0 || pct > 100) {
        newErrors.fees = `Fee % for ${pl} must be between 0 and 100`;
        ok = false;
        break;
      }
    }

    setErrors(newErrors);
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setProcessing(true);

      const alignedFees = ensureFeesForPlatforms(
        formData.platforms,
        formData.fees
      ).map((f) => ({
        platform: f.platform,
        feePercentage: Number(clamp(f.feePercentage, 0, 100)),
      }));

      const finalType =
        formData.type === "other" ? formData.customType.trim() : formData.type;

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone || "",
        notes: formData.notes || "",
        platforms: formData.platforms,
        type: finalType || "",
        fees: alignedFees,
      };

      let res;
      if (isEditing && providerId) {
        res = await providersService.update(providerId, payload);
      } else {
        res = await providersService.create(payload);
      }

      if (res?.data && (res.data.statusCode === 200 || res.data.statusCode === 201)) {
        onSuccess?.();
        onClose();
      } else {
        const msg = res?.data?.message || "Operation failed";
        setErrors((prev) => ({ ...prev, form: msg }));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Operation failed";
      if (String(msg).toLowerCase().includes("email already exists")) {
        setErrors((prev) => ({
          ...prev,
          email: "Provider email already exists",
        }));
      } else {
        setErrors((prev) => ({ ...prev, form: msg }));
      }
      console.error("Provider save failed:", err);
    } finally {
      setProcessing(false);
    }
  };

  /* Styles (simplified, minimal) */
  const tileBase =
    "flex items-center gap-2 px-3 py-2 rounded-xl border text-sm bg-white dark:bg-neutral-900 transition-all cursor-pointer select-none";
  const tileOff =
    "border-neutral-200 dark:border-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600";
  const tileOn =
    "border-neutral-900 dark:border-neutral-100 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900";

  const checkBase =
    "h-4 w-4 rounded-full border flex items-center justify-center text-[10px] font-medium shrink-0";
  const checkOn = "border-white bg-white text-neutral-900";
  const checkOff =
    "border-neutral-300 dark:border-neutral-600 bg-transparent text-transparent";

  const headerBadge =
    "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700";

  const headerTypeLabel =
    formData.type === "other"
      ? formData.customType || "Other"
      : TYPES.find((t) => t.value === formData.type)?.name || formData.type;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Panel */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-1"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-1"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl ring-1 ring-black/5 dark:ring-white/10 transition-all">
                {/* Header */}
                <div className="border-b border-neutral-200/80 dark:border-neutral-800 bg-gradient-to-r from-neutral-900 to-neutral-800 px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <Dialog.Title className="text-lg font-semibold text-neutral-50">
                        {isEditing ? "Update Provider" : "Add Provider"}
                      </Dialog.Title>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                        <span>
                          {formData.name || (isEditing ? "Existing provider" : "New provider")}
                        </span>
                        {(formData.type || formData.customType) && (
                          <span className={headerBadge}>{headerTypeLabel}</span>
                        )}
                        {formData._id && (
                          <span className="inline-flex items-center gap-1">
                            <Hash size={10} />
                            {formData._id.slice(-6)}
                          </span>
                        )}
                        {isEditing && (formData.createdAt || formData.updatedAt) && (
                          <>
                            {formData.createdAt && (
                              <span>Created: {formatDate(formData.createdAt)}</span>
                            )}
                            {formData.updatedAt && (
                              <span>Updated: {formatDate(formData.updatedAt)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="rounded-full p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-5 py-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-10">
                      <RotateCw
                        size={22}
                        className="animate-spin text-neutral-500 dark:text-neutral-400"
                      />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Basic Info */}
                      <section className="space-y-4">
                        <h3 className="text-xs font-medium uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
                          Basic information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <InputCustom
                              label="Provider name"
                              placeholder="e.g. John Media"
                              value={formData.name}
                              onChange={(v) => handleChange("name", v)}
                              icon={
                                <User
                                  size={15}
                                  className="text-neutral-400 dark:text-neutral-500"
                                />
                              }
                            />
                            {errors.name && (
                              <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={11} /> {errors.name}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                              Type <span className="text-red-500">*</span>
                            </label>
                            <SearchableSelect
                              options={TYPES}
                              value={formData.type}
                              onChange={(v) => handleChange("type", v)}
                              placeholder="Select type"
                              icon={<TypeIcon size={15} />}
                            />
                            {formData.type === "other" && (
                              <div className="mt-2">
                                <InputCustom
                                  label="Custom type"
                                  placeholder="Write provider type"
                                  value={formData.customType}
                                  onChange={(v) => handleChange("customType", v)}
                                  icon={
                                    <TypeIcon
                                      size={15}
                                      className="text-neutral-400 dark:text-neutral-500"
                                    />
                                  }
                                />
                              </div>
                            )}
                            {errors.type && (
                              <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={11} /> {errors.type}
                              </p>
                            )}
                          </div>

                          <div>
                            <InputCustom
                              label="Email"
                              type="email"
                              placeholder="name@company.com"
                              value={formData.email}
                              onChange={(v) => handleChange("email", v)}
                              icon={
                                <Mail
                                  size={15}
                                  className="text-neutral-400 dark:text-neutral-500"
                                />
                              }
                            />
                            {errors.email && (
                              <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={11} /> {errors.email}
                              </p>
                            )}
                          </div>

                          <div>
                            <InputCustom
                              label="Phone"
                              placeholder="+212 6 12 34 56 78"
                              value={formData.phone}
                              onChange={(v) => handleChange("phone", v)}
                              icon={
                                <Phone
                                  size={15}
                                  className="text-neutral-400 dark:text-neutral-500"
                                />
                              }
                            />
                          </div>
                        </div>
                      </section>

                      {/* Platforms & Fees */}
                      <section className="space-y-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                            <h3 className="text-xs font-medium uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
                              Platforms & fees
                            </h3>
                          </div>
                          {errors.platforms && (
                            <p className="text-[11px] text-red-500 flex items-center gap-1">
                              <AlertCircle size={11} /> {errors.platforms}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                          {PLATFORMS.map(({ id, name, Icon }) => {
                            const selected = formData.platforms.includes(id);
                            return (
                              <label
                                key={id}
                                htmlFor={`pl-${id}`}
                                className={`${tileBase} ${selected ? tileOn : tileOff}`}
                              >
                                <input
                                  id={`pl-${id}`}
                                  type="checkbox"
                                  className="sr-only"
                                  checked={selected}
                                  onChange={() => togglePlatform(id)}
                                />
                                <span className={`${checkBase} ${selected ? checkOn : checkOff}`}>
                                  <Check size={10} />
                                </span>
                                <span className="inline-flex  text-neutral-800 dark:text-neutral-100 items-center gap-1.5 text-[13px]">
                                  <Icon />
                                  <span>{name}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>

                        {formData.platforms.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Percent className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                              <p className="text-xs font-medium text-neutral-700 dark:text-neutral-100">
                                Fee per platform
                              </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                              {formData.platforms.map((pl) => {
                                const fee =
                                  formData.fees.find((f) => f.platform === pl) ||
                                  { feePercentage: 0 };
                                const invalid =
                                  fee.feePercentage < 0 || fee.feePercentage > 100;
                                const PIcon =
                                  PLATFORMS.find((p) => p.id === pl)?.Icon ||
                                  (() => null);

                                return (
                                  <div
                                    key={pl}
                                    className={`rounded-xl border bg-neutral-50/60 dark:bg-neutral-900 px-3 py-2.5 ${invalid
                                      ? "border-red-300 dark:border-red-500/60"
                                      : "border-neutral-200 dark:border-neutral-800"
                                      }`}
                                  >
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                      <div className="flex items-center gap-2 text-xs text-neutral-700 dark:text-neutral-200">
                                        <PIcon />
                                        <span>{pl}</span>
                                      </div>
                                      <span className="text-[11px] text-neutral-400">
                                        0 – 100 %
                                      </span>
                                    </div>
                                    <div className="relative">
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step="0.1"
                                        value={fee.feePercentage}
                                        onChange={(e) =>
                                          handleFeeChange(pl, e.target.value)
                                        }
                                        className={`w-full pr-8 px-2.5 py-1.5 text-sm rounded-xl border bg-white dark:bg-neutral-950 text-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-1 ${invalid
                                          ? "border-red-300 dark:border-red-500/60 focus:ring-red-500/60"
                                          : "border-neutral-200 dark:border-neutral-700 focus:ring-neutral-800/60"
                                          }`}
                                        placeholder="0"
                                      />
                                      <span className="absolute inset-y-0 right-2 flex items-center text-[11px] text-neutral-400">
                                        %
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {errors.fees && (
                              <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={11} /> {errors.fees}
                              </p>
                            )}
                          </div>
                        )}
                      </section>

                      {/* Notes */}
                      <section className="space-y-2">
                        <label className="flex items-center gap-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                          <FileText className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                          Notes (optional)
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => handleChange("notes", e.target.value)}
                          placeholder="Internal notes about this provider…"
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-800 rounded-xl bg-neutral-50 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-neutral-800/60"
                        />
                      </section>

                      {/* Info + errors */}
                      <section className="rounded-xl border border-neutral-200/80 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-900 px-3.5 py-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle
                            size={15}
                            className="mt-0.5 text-neutral-500 dark:text-neutral-400"
                          />
                          <div className="space-y-1">
                            <p className="text-[11px] leading-relaxed text-neutral-600 dark:text-neutral-300">
                              {isEditing
                                ? "Adjust provider details and platform fees. Changes will apply to all future usage."
                                : "Add the provider’s contact details, select the platforms they work on, and set a fee percentage for each one."}
                            </p>
                            {errors.form && (
                              <p className="text-[11px] text-red-500 flex items-center gap-1">
                                <AlertCircle size={11} /> {errors.form}
                              </p>
                            )}
                          </div>
                        </div>
                      </section>
                    </form>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200/80 dark:border-neutral-800 px-5 py-3.5 flex justify-end gap-2.5 bg-neutral-50/60 dark:bg-neutral-950">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-1.5 text-sm font-[400] text-neutral-700 bg-white border border-neutral-200 rounded-full hover:bg-neutral-50 dark:bg-neutral-950 dark:text-neutral-200 dark:border-neutral-700 dark:hover:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300/70"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={processing || loading}
                    className="inline-flex items-center px-6 py-1.5 text-sm font-[400] text-white rounded-full bg-neutral-900 hover:bg-black dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/40 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isEditing ? "Update" : "Save"}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
